import { useLang } from "../../context/LanguageContext";

function passportScanExampleUrl(filename: string): string {
  const base = typeof process !== "undefined" && process.env.PUBLIC_URL ? String(process.env.PUBLIC_URL).replace(/\/$/, "") : "";
  const suffix = `/passport-scan-examples/${filename}`;
  return base ? `${base}${suffix}` : suffix;
}

const scanExampleImages = {
  acceptable: passportScanExampleUrl("acceptable.png"),
  rejectTooDark: passportScanExampleUrl("reject-too-dark.png"),
  rejectBlurry: passportScanExampleUrl("reject-blurry.png"),
  rejectFingers: passportScanExampleUrl("reject-fingers.png"),
} as const;

export interface PassportScanExamplesModalProps {
  onClose: () => void;
}

/**
 * Passport-specific bottom sheet with acceptable vs rejected scan imagery (static PNGs under ``public/passport-scan-examples/``).
 */
export default function PassportScanExamplesModal({ onClose }: PassportScanExamplesModalProps) {
  const { t } = useLang();

  const rejectedCards: {
    src: string;
    titleKey:
      | "docUpload.passportScanExamplesRejectDarkTitle"
      | "docUpload.passportScanExamplesRejectBlurTitle"
      | "docUpload.passportScanExamplesRejectFingersTitle";
    descKey:
      | "docUpload.passportScanExamplesRejectDarkDesc"
      | "docUpload.passportScanExamplesRejectBlurDesc"
      | "docUpload.passportScanExamplesRejectFingersDesc";
  }[] = [
    {
      src: scanExampleImages.rejectTooDark,
      titleKey: "docUpload.passportScanExamplesRejectDarkTitle",
      descKey: "docUpload.passportScanExamplesRejectDarkDesc",
    },
    {
      src: scanExampleImages.rejectBlurry,
      titleKey: "docUpload.passportScanExamplesRejectBlurTitle",
      descKey: "docUpload.passportScanExamplesRejectBlurDesc",
    },
    {
      src: scanExampleImages.rejectFingers,
      titleKey: "docUpload.passportScanExamplesRejectFingersTitle",
      descKey: "docUpload.passportScanExamplesRejectFingersDesc",
    },
  ];

  return (
    <div
      className="fixed inset-0 z-[176] flex flex-col justify-end bg-black/45"
      role="dialog"
      aria-modal="true"
      aria-labelledby="gm-passport-scan-examples-title"
      onClick={onClose}
    >
      <div
        className="w-full max-w-[480px] mx-auto flex flex-col max-h-[min(92dvh,920px)] rounded-t-[22px] bg-[#f9f8f6] shadow-2xl overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        <header className="shrink-0 grid grid-cols-[44px_1fr_44px] items-center gap-1 px-2 pt-[max(14px,env(safe-area-inset-top))] pb-3 border-b border-slate-200/90 bg-[#f9f8f6]">
          <button
            type="button"
            onClick={onClose}
            aria-label={t("docUpload.passportScanExamplesCloseAria")}
            className="w-11 h-11 flex items-center justify-center rounded-full text-slate-500 hover:bg-slate-200/60 hover:text-slate-700 transition-colors touch-manipulation justify-self-start"
          >
            <i className="ri-close-line text-[22px]" aria-hidden="true" />
          </button>
          <h2 id="gm-passport-scan-examples-title" className="text-[17px] font-bold text-slate-900 text-center min-w-0 px-1">
            {t("docUpload.passportScanExamplesTitle")}
          </h2>
          <span className="w-11 shrink-0" aria-hidden="true" />
        </header>

        <div className="flex-1 min-h-0 overflow-y-auto overscroll-contain px-4 pt-4 pb-3">
          <p className="text-[15px] text-slate-700 leading-relaxed mb-6">{t("docUpload.passportScanExamplesIntro")}</p>

          <p className="text-[12px] font-bold tracking-[0.08em] text-emerald-700 uppercase mb-3">
            {t("docUpload.passportScanExamplesAcceptableLabel")}
          </p>
          <div className="rounded-2xl bg-white border border-slate-200/90 shadow-sm overflow-hidden mb-8">
            <img
              src={scanExampleImages.acceptable}
              alt=""
              className="w-full h-auto object-cover block bg-white"
              decoding="async"
              loading="eager"
            />
            <p className="text-[14px] text-slate-900 leading-snug px-4 py-3 border-t border-slate-100">
              {t("docUpload.passportScanExamplesAcceptableCaption")}
            </p>
          </div>

          <p className="text-[12px] font-bold tracking-[0.06em] text-red-700 uppercase mb-3">
            {t("docUpload.passportScanExamplesRejectedLabel")}
          </p>
          <div className="flex flex-col gap-5 pb-2">
            {rejectedCards.map((card) => (
              <div
                key={card.src}
                className="rounded-2xl bg-white border border-slate-200/90 shadow-sm overflow-hidden"
              >
                <img src={card.src} alt="" className="w-full h-auto object-cover block bg-white" decoding="async" loading="lazy" />
                <div className="px-4 py-3 border-t border-slate-100">
                  <p className="text-[15px] font-bold text-slate-900">{t(card.titleKey)}</p>
                  <p className="text-[14px] text-slate-600 mt-1 leading-snug">{t(card.descKey)}</p>
                </div>
              </div>
            ))}
          </div>
        </div>

        <footer className="shrink-0 px-4 pt-2 pb-[max(16px,env(safe-area-inset-bottom))] bg-[#f9f8f6] border-t border-slate-200/80">
          <button
            type="button"
            onClick={onClose}
            className="w-full min-h-[52px] rounded-xl border border-slate-300 bg-white text-slate-900 text-[15px] font-semibold hover:bg-slate-50 transition-colors touch-manipulation"
          >
            {t("docUpload.passportScanExamplesGotIt")}
          </button>
        </footer>
      </div>
    </div>
  );
}
